import React, { useState, useEffect } from 'react';
import '../../styles/ShiftCalendar.css';
import Loading from '../common/Loading';

const ShiftsPage = () => {
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 14)) // 表示期間を2週間に短縮
  });
  const [viewMode, setViewMode] = useState('week'); // day, week, month
  const [displaySettings, setDisplaySettings] = useState({
    showRequestedShifts: true,
    showConfirmedShifts: true,
    showWorkingHours: true,
    showPersonalInfo: true,
    showShiftCount: true,
    showStaffType: true,
  });
  const [editingShift, setEditingShift] = useState(null);
  const [showShiftModal, setShowShiftModal] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // In a real implementation, you would make API calls to get shift and staff data
        // For now, we'll simulate this with mock data
        const mockStaff = generateMockStaff();
        const mockShifts = generateMockShifts(mockStaff, dateRange);
        
        setStaff(mockStaff);
        setShifts(mockShifts);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [dateRange]);

  // Generate mock data for demonstration
  const generateMockStaff = () => {
    return [
      { id: 1, name: '高橋 真史', type: '社員', skills: ['バリスタ', 'レジ', '開店'] },
      { id: 2, name: '鈴木 健太', type: '社員', skills: ['キッチン', 'ホール', '閉店'] },
      { id: 3, name: '佐藤 明子', type: 'パート', skills: ['レジ', 'ホール'] },
      { id: 4, name: '田中 優', type: 'パート', skills: ['キッチン', 'ホール'] },
      { id: 5, name: '山田 花子', type: 'アルバイト', skills: ['ホール'] },
      { id: 6, name: '伊藤 拓也', type: 'アルバイト', skills: ['レジ'] },
      { id: 7, name: '木村 誠', type: '単発バイト', skills: ['ホール'] },
      { id: 8, name: '佐佐木 花子', type: 'パート', skills: ['レジ', 'ホール'] },
    ];
  };

  const generateMockShifts = (mockStaff, dateRange) => {
    const shifts = [];
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const currentDate = new Date(d);
      
      // Skip generating shifts for some days to make the calendar look more realistic
      if (Math.random() > 0.9) continue;
      
      mockStaff.forEach(staffMember => {
        // Skip generating shifts for some staff members on some days
        if (Math.random() > 0.7) return;
        
        // Generate random shift times
        const startHour = 7 + Math.floor(Math.random() * 10);
        const endHour = startHour + 5 + Math.floor(Math.random() * 4);
        
        shifts.push({
          id: `shift-${currentDate.toISOString()}-${staffMember.id}`,
          staffId: staffMember.id,
          date: new Date(currentDate),
          startTime: `${startHour}:00`,
          endTime: `${endHour}:00`,
          status: Math.random() > 0.2 ? 'confirmed' : 'requested',
          storeId: Math.floor(Math.random() * 3) + 1 // 1, 2, or 3
        });
      });
    }
    
    return shifts;
  };

  // Handle date range changes
  const handlePrevPeriod = () => {
    const newStartDate = new Date(dateRange.startDate);
    const newEndDate = new Date(dateRange.endDate);
    
    switch (viewMode) {
      case 'day':
        newStartDate.setDate(newStartDate.getDate() - 1);
        newEndDate.setDate(newEndDate.getDate() - 1);
        break;
      case 'week':
        newStartDate.setDate(newStartDate.getDate() - 7);
        newEndDate.setDate(newEndDate.getDate() - 7);
        break;
      case 'month':
      default:
        newStartDate.setMonth(newStartDate.getMonth() - 1);
        newEndDate.setMonth(newEndDate.getMonth() - 1);
        break;
    }
    
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  const handleNextPeriod = () => {
    const newStartDate = new Date(dateRange.startDate);
    const newEndDate = new Date(dateRange.endDate);
    
    switch (viewMode) {
      case 'day':
        newStartDate.setDate(newStartDate.getDate() + 1);
        newEndDate.setDate(newEndDate.getDate() + 1);
        break;
      case 'week':
        newStartDate.setDate(newStartDate.getDate() + 7);
        newEndDate.setDate(newEndDate.getDate() + 7);
        break;
      case 'month':
      default:
        newStartDate.setMonth(newStartDate.getMonth() + 1);
        newEndDate.setMonth(newEndDate.getMonth() + 1);
        break;
    }
    
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  const handleTodayClick = () => {
    const today = new Date();
    let newEndDate;
    
    switch (viewMode) {
      case 'day':
        newEndDate = new Date(today);
        break;
      case 'week':
        newEndDate = new Date(today);
        newEndDate.setDate(newEndDate.getDate() + 6);
        break;
      case 'month':
      default:
        newEndDate = new Date(today);
        newEndDate.setDate(newEndDate.getDate() + 30);
        break;
    }
    
    setDateRange({ startDate: today, endDate: newEndDate });
  };

  // Change view mode (day, week, month)
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    
    const newStartDate = new Date();
    let newEndDate;
    
    switch (mode) {
      case 'day':
        newEndDate = new Date(newStartDate);
        break;
      case 'week':
        newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + 6);
        break;
      case 'month':
      default:
        newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + 30);
        break;
    }
    
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
  };

  // Toggle display settings
  const handleDisplaySettingChange = (setting) => {
    setDisplaySettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  // シフトをクリックした時の処理
  const handleShiftClick = (shift) => {
    setEditingShift(shift);
    setShowShiftModal(true);
  };

  // シフトの追加
  const handleAddShift = (date, staffId) => {
    const newShift = {
      id: `shift-new-${Date.now()}`,
      staffId: staffId,
      date: new Date(date),
      startTime: '10:00',
      endTime: '17:00',
      status: 'confirmed',
      storeId: 1
    };
    
    setEditingShift(newShift);
    setShowShiftModal(true);
  };

  // シフトの保存
  const handleSaveShift = (shift) => {
    const isNewShift = !shifts.some(s => s.id === shift.id);
    
    if (isNewShift) {
      setShifts([...shifts, shift]);
    } else {
      setShifts(shifts.map(s => s.id === shift.id ? shift : s));
    }
    
    setShowShiftModal(false);
    setEditingShift(null);
  };

  // シフトの削除
  const handleDeleteShift = (shiftId) => {
    setShifts(shifts.filter(s => s.id !== shiftId));
    setShowShiftModal(false);
    setEditingShift(null);
  };

  // Generate dates array for the calendar
  const getDates = () => {
    const dates = [];
    const currentDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Get shifts for a staff member on a specific date
  const getShiftsForStaffOnDate = (staffId, date) => {
    return shifts.filter(shift => 
      shift.staffId === staffId && 
      shift.date.toDateString() === date.toDateString()
    );
  };

  // Format time for display
  const formatTime = (timeString) => {
    return timeString;
  };

  // Format date for display
  const formatDate = (date) => {
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    const dayOfWeek = days[date.getDay()];
    return `${date.getDate()} (${dayOfWeek})`;
  };

  // Format month/year for display
  const formatMonthYear = (date) => {
    return `${date.getFullYear()}年${date.getMonth() + 1}月`;
  };

  // 店舗名を取得（実際の実装ではAPIから取得）
  const getStoreName = (storeId) => {
    const stores = {
      1: '本店',
      2: '西口店',
      3: '駅前店'
    };
    return stores[storeId] || '不明';
  };

  // Render staff categories
  const renderStaffCategories = () => {
    const categories = ['社員', 'パート', 'アルバイト', '単発バイト'];
    
    return categories.map(category => {
      const categoryStaff = staff.filter(s => s.type === category);
      
      if (categoryStaff.length === 0) return null;
      
      return (
        <div key={category} className="staff-category">
          <div className="category-header">
            <h3>{category}</h3>
          </div>
          {categoryStaff.map(s => renderStaffRow(s))}
        </div>
      );
    });
  };

  // Render a row for a staff member
  const renderStaffRow = (staffMember) => {
    const dates = getDates();
    
    return (
      <div key={staffMember.id} className="staff-row">
        <div className="staff-info">
          <span className="staff-name">{staffMember.name}</span>
        </div>
        <div className="staff-shifts">
          {dates.map(date => {
            const shiftsOnDate = getShiftsForStaffOnDate(staffMember.id, date);
            return (
              <div key={`${staffMember.id}-${date}`} className="date-cell">
                {shiftsOnDate.length === 0 && (
                  <div 
                    className="add-shift-placeholder"
                    onClick={() => handleAddShift(date, staffMember.id)}
                  >
                    <span className="add-icon">+</span>
                  </div>
                )}
                {shiftsOnDate.map(shift => (
                  <div 
                    key={shift.id} 
                    className={`shift-block ${shift.status}`}
                    style={{
                      backgroundColor: shift.status === 'confirmed' ? '#36A2EB' : '#FFCE56'
                    }}
                    onClick={() => handleShiftClick(shift)}
                  >
                    <div className="shift-time">
                      {formatTime(shift.startTime)}<br /> 
                      ～<br />
                      {formatTime(shift.endTime)}
                    </div>
                    <div className="shift-store">{getStoreName(shift.storeId)}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // シフト編集モーダル
  const renderShiftModal = () => {
    if (!showShiftModal) return null;
    
    const isNewShift = !shifts.some(s => s.id === editingShift.id);
    const modalTitle = isNewShift ? 'シフトを追加' : 'シフトを編集';
    
    const [shiftData, setShiftData] = useState({
      ...editingShift,
      date: editingShift.date.toISOString().split('T')[0],
    });
    
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setShiftData({
        ...shiftData,
        [name]: value
      });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      
      const updatedShift = {
        ...shiftData,
        date: new Date(shiftData.date),
        storeId: parseInt(shiftData.storeId, 10)
      };
      
      handleSaveShift(updatedShift);
    };
    
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{modalTitle}</h3>
            <button 
              className="modal-close-btn"
              onClick={() => setShowShiftModal(false)}
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="date">日付</label>
              <input 
                type="date" 
                id="date" 
                name="date"
                value={shiftData.date}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="startTime">開始時間</label>
              <input 
                type="time" 
                id="startTime" 
                name="startTime"
                value={shiftData.startTime}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="endTime">終了時間</label>
              <input 
                type="time" 
                id="endTime" 
                name="endTime"
                value={shiftData.endTime}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="storeId">店舗</label>
              <select 
                id="storeId" 
                name="storeId"
                value={shiftData.storeId}
                onChange={handleInputChange}
                required
              >
                <option value="1">本店</option>
                <option value="2">西口店</option>
                <option value="3">駅前店</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="status">ステータス</label>
              <select 
                id="status" 
                name="status"
                value={shiftData.status}
                onChange={handleInputChange}
                required
              >
                <option value="requested">希望</option>
                <option value="confirmed">確定</option>
              </select>
            </div>
            
            <div className="form-actions">
              {!isNewShift && (
                <button 
                  type="button" 
                  className="delete-btn"
                  onClick={() => handleDeleteShift(shiftData.id)}
                >
                  削除
                </button>
              )}
              <button type="submit" className="save-btn">保存</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="shifts-page">
      <h2>シフト管理</h2>
      <p>シフトの作成、編集、表示ができます</p>
      
      <div className="shift-controls">
        <div className="view-modes">
          <button 
            className={viewMode === 'day' ? 'active' : ''} 
            onClick={() => handleViewModeChange('day')}
          >
            日
          </button>
          <button 
            className={viewMode === 'week' ? 'active' : ''} 
            onClick={() => handleViewModeChange('week')}
          >
            週
          </button>
          <button 
            className={viewMode === 'month' ? 'active' : ''} 
            onClick={() => handleViewModeChange('month')}
          >
            月
          </button>
          <button 
            className="print-preview-btn"
          >
            カレンダー印刷プレビュー
          </button>
        </div>
        
        <div className="date-selector">
          <button onClick={handlePrevPeriod}>&lt;</button>
          <div className="date-range">
            <span>{formatMonthYear(dateRange.startDate)}</span>
          </div>
          <button onClick={handleNextPeriod}>&gt;</button>
          <button onClick={handleTodayClick}>今日</button>
        </div>
        
        <div className="display-settings">
          <details>
            <summary>表示設定</summary>
            <div className="settings-options">
              <label>
                <input 
                  type="checkbox" 
                  checked={displaySettings.showRequestedShifts}
                  onChange={() => handleDisplaySettingChange('showRequestedShifts')}
                />
                希望シフト
              </label>
              <label>
                <input 
                  type="checkbox" 
                  checked={displaySettings.showConfirmedShifts}
                  onChange={() => handleDisplaySettingChange('showConfirmedShifts')}
                />
                確定シフト
              </label>
              <label>
                <input 
                  type="checkbox" 
                  checked={displaySettings.showWorkingHours}
                  onChange={() => handleDisplaySettingChange('showWorkingHours')}
                />
                労働時間
              </label>
              <label>
                <input 
                  type="checkbox" 
                  checked={displaySettings.showPersonalInfo}
                  onChange={() => handleDisplaySettingChange('showPersonalInfo')}
                />
                個人情報
              </label>
            </div>
          </details>
        </div>
      </div>
      
      <div className="shift-calendar">
        <div className="calendar-header">
          <div className="staff-column-header">スタッフ</div>
          <div className="dates-header">
            {getDates().map(date => (
              <div key={date.toString()} className="date-header">
                {formatDate(date)}
              </div>
            ))}
          </div>
        </div>
        
        <div className="calendar-body">
          {renderStaffCategories()}
        </div>
      </div>
      
      <div className="shift-actions">
        <button className="create-shift-btn">シフト作成アシストを実行する</button>
        <button className="share-shifts-btn">シフト表を共有・印刷する</button>
      </div>
      
      {showShiftModal && renderShiftModal()}
    </div>
  );
};

export default ShiftsPage;
